import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { Search, Filter, AlertTriangle } from 'lucide-react';

// ✅ Helper function for badge colors
function getStatusColor(status) {
  switch (status) {
    case 'On Watchlist': return 'danger';
    case 'Cleared': return 'secondary';
    case 'Verified': return 'primary';
    case 'Not Verified': return 'warning';
    default: return 'secondary';
  }
}

// ✅ Mock Watchlist Data (40 persons)
const mockWatchlist = Array.from({ length: 40 }, (_, i) => ({
  id: i + 1,
  name: [
    'Emeka Obi', 'Fatima Sule', 'John Ade', 'Chika Nwosu',
    'Bello Musa', 'Aminu Yusuf', 'Sandra Okeke', 'Peter Bassey',
    'Ngozi Eze', 'Hassan Umar'
  ][Math.floor(Math.random() * 10)],
  date: new Date(2025, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1)
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
  reason: [
    'Under Investigation', 
    'Travel Ban', 
    'Pending Court Case', 
    'Security Alert'
  ][Math.floor(Math.random() * 4)],
  watchStatus: Math.random() > 0.4 ? 'On Watchlist' : 'Cleared',
  verifyStatus: Math.random() > 0.5 ? 'Verified' : 'Not Verified',
  img: require('../assets/user2.png') // ✅ You can swap user images (user1.png, user2.png)
}));

export default function WatchListPage() {
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [tab, setTab] = useState('Active'); // Active or Cleared
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;

  // ✅ Filter Watchlist
  const filteredWatchlist = mockWatchlist.filter(person =>
    (tab === 'Active' ? person.watchStatus === 'On Watchlist' : person.watchStatus === 'Cleared') &&
    person.name.toLowerCase().includes(search.toLowerCase())
  );

  // ✅ Pagination
  const totalPages = Math.ceil(filteredWatchlist.length / perPage);
  const displayedPersons = filteredWatchlist.slice((page - 1) * perPage, page * perPage);

  return (
    <div className="d-flex vh-100 overflow-hidden">
      {/* ✅ Sidebar */}
      <Sidebar
        active="watchlist"
        mobileSidebarOpen={mobileSidebarOpen}
        setMobileSidebarOpen={setMobileSidebarOpen}
      />

      {/* ✅ Main Content */}
      <div className="flex-grow-1 d-flex flex-column bg-light">
        <Header 
          darkMode={darkMode} 
          setDarkMode={setDarkMode} 
          toggleSidebar={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          notifications={3}   
        />               
        
        {/* ✅ Modern Top Bar */}
        <div className="p-4">
          <div className="d-flex align-items-center justify-content-between bg-white rounded p-3">
            <h4 className="m-0 fw-bold cool-font">Watchlist</h4>
            <div className="d-flex align-items-center gap-2">
              
              {/* Modern Search Box */}
              <div className="position-relative">
                <Search 
                  size={18} 
                  className="position-absolute top-50 start-2 translate-middle-y text-muted" 
                  style={{ left: '10px' }} 
                />
                <input
                  type="text"
                  className="form-control ps-5 rounded-pill"
                  placeholder="Search flagged persons..."
                  style={{ width: '250px', transition: '0.3s ease all' }}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>

              {/* Filter Button */}
              <button className="btn btn-outline-primary rounded-pill px-3 d-flex align-items-center gap-1">
                <Filter size={16} /> Filter
              </button>
            </div>
          </div>
        </div>

        {/* ✅ Main Scrollable Area */}
        <main className="flex-grow-1 overflow-auto p-4">

          {/* ✅ Switcher */}
          <div className="d-flex justify-content-center mb-4">
            <div className="btn-group w-50">
              <button
                className={`btn ${tab === 'Active' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setTab('Active'); setPage(1); }}
              >
                Active Watchlist
              </button>
              <button
                className={`btn ${tab === 'Cleared' ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => { setTab('Cleared'); setPage(1); }}
              >
                Cleared Persons
              </button>
            </div>
          </div>

          {/* ✅ Empty State */}
          {displayedPersons.length === 0 ? (
            <div className="text-center mt-5">
              <AlertTriangle size={64} className="text-muted mb-3" />
              <h5 className="fw-bold">No Flagged Persons Found</h5>
              <p className="text-muted">Try adjusting your filters or search.</p>
              <button 
                className="btn btn-danger mt-2"
                onClick={() => setSearch('')}
              >
                Clear Filters
              </button>
            </div>
          ) : (
            <>
              {/* ✅ Watchlist Cards */}
              <div className="row g-3">
                {displayedPersons.map((person) => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3" key={person.id}>
                    <div className="card border h-100 text-center p-3">

                      {/* 🖼 Image */}
                      <div
                        className="mx-auto rounded overflow-hidden mb-3"
                        style={{ width: '120px', height: '120px', background: '#f0f0f0' }}
                      >
                        <img
                          src={person.img}
                          alt={person.name}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>

                      {/* 📝 Details */}
                      <h6 className="fw-bold">{person.name}</h6>
                      <p className="small text-muted mb-1">{person.date}</p>
                      <p className="small text-danger fw-bold mb-2">{person.reason}</p>

                      {/* ✅ Status Badges */}
                      <div className="mb-2">
                        <span className={`badge bg-${getStatusColor(person.watchStatus)} me-1`}>
                          {person.watchStatus}
                        </span>
                        <span className={`badge bg-${getStatusColor(person.verifyStatus)}`}>
                          {person.verifyStatus}
                        </span>
                      </div>

                      {/* 🔘 Button */}
                      <button className="btn btn-danger btn-sm w-100 mt-auto">View Details</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* ✅ Pagination */}
              <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === 1}
                  onClick={() => setPage(page - 1)}
                >
                  ‹ Prev
                </button>
                <span className="fw-bold">{page} / {totalPages}</span>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  disabled={page === totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next ›
                </button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
